/**
 * The ordered list every editorial picker uses (Figma CMS-06 "Canciones de la lista", CMS-08
 * "Items de la fila"): a numbered row with a drag handle, ←/→ move buttons and a remove button.
 *
 * Dragging is native HTML5 DnD (no new dependency); the two buttons keep the control usable
 * with a keyboard and make the ordering deterministic in tests, exactly like the artist credits
 * of WP3.2.
 */
import { useState } from 'react';

import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface OrderedEntry {
  /** Stable key — the entity id. */
  key: string;
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  /** Flat swatch color when there is no image. */
  color?: string;
  /** Shown after the title (e.g. "borrador"). */
  tag?: string;
  warn?: boolean;
}

export interface OrderedListProps {
  entries: OrderedEntry[];
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
  emptyLabel: string;
  'data-testid'?: string;
}

/** Deterministic flat color for an id, mirroring the TV cards (docs/04 §3). */
const SWATCHES = ['#5b8cff', '#ff7359', '#c65cff', '#19e3ff', '#ff2d87', '#7ed957', '#3ed6a5'];
export function swatch(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return SWATCHES[hash % SWATCHES.length]!;
}

export function OrderedList({
  entries,
  onMove,
  onRemove,
  disabled = false,
  emptyLabel,
  ...rest
}: OrderedListProps) {
  const { t } = useT();
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  if (entries.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-[12px] text-muted">
        {emptyLabel}
      </p>
    );
  }

  return (
    <ul
      className="divide-y divide-line overflow-hidden rounded-lg border border-line"
      data-testid={rest['data-testid']}
    >
      {entries.map((entry, index) => (
        <li
          key={entry.key}
          draggable={!disabled}
          onDragStart={() => setDragIndex(index)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (dragIndex !== null) onMove(dragIndex, index);
            setDragIndex(null);
          }}
          onDragEnd={() => setDragIndex(null)}
          data-testid={`ordered-item-${entry.key}`}
          className={cn(
            'flex items-center gap-2 bg-panel px-2 py-1.5',
            dragIndex === index && 'opacity-50',
          )}
        >
          <span aria-hidden="true" className="cursor-grab px-1 text-muted">
            ⠿
          </span>
          <span className="w-5 shrink-0 text-right text-[12px] tabular-nums text-muted">
            {index + 1}
          </span>
          {entry.imageUrl ? (
            <img src={entry.imageUrl} alt="" className="size-8 shrink-0 rounded-md object-cover" />
          ) : (
            <span
              aria-hidden="true"
              className="size-8 shrink-0 rounded-md"
              style={{ background: entry.color ?? swatch(entry.key) }}
            />
          )}
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[13px] font-semibold text-ink">{entry.title}</span>
              {entry.tag && (
                <span
                  className={cn(
                    'shrink-0 rounded-full px-1.5 py-0.5 text-[12px] font-semibold',
                    entry.warn ? 'bg-warn/15 text-warn-ink' : 'bg-canvas text-muted',
                  )}
                >
                  {entry.tag}
                </span>
              )}
            </span>
            {entry.subtitle && (
              <span className="block truncate text-[12px] text-muted">{entry.subtitle}</span>
            )}
          </span>
          <button
            type="button"
            disabled={disabled || index === 0}
            onClick={() => onMove(index, index - 1)}
            aria-label={t('cms:editorial.moveUp', { title: entry.title })}
            className="px-1 text-muted hover:text-ink disabled:opacity-30"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={disabled || index === entries.length - 1}
            onClick={() => onMove(index, index + 1)}
            aria-label={t('cms:editorial.moveDown', { title: entry.title })}
            className="px-1 text-muted hover:text-ink disabled:opacity-30"
          >
            ↓
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onRemove(index)}
            aria-label={t('cms:editorial.remove', { title: entry.title })}
            className="rounded-full px-1.5 text-muted hover:bg-line hover:text-ink"
          >
            ✕
          </button>
        </li>
      ))}
    </ul>
  );
}
